import React, { useContext } from 'react'
import { injectIntl, InjectedIntlProps } from 'gatsby-plugin-intl'
import FeaturesImpress from '~/components/FeaturesImpress'
import DarkCard from '~/components/DarkCard'
import { H2, Center, Spacing, Text, ThemeContext, View } from '~/pagui'
import Header from '~/components/Header'
import { LG_SIZE } from '~/pagui/components/View'
import { css } from '@emotion/native'
import { SEO } from '~/components/SEO'
import LinkButton from './../../components/LinkButton'
import FeaturesLayout from '~/components/Layout/FeaturesLayout'
import GreenCircle from '~/components/GreenCircle'
import BetaSubscription from '../../components/BetaSubscription'

type Props = {} & InjectedIntlProps

const title = css({
  fontSize: 50,
  lineHeight: 50,
  position: 'relative',
})
const titleLG = css({
  fontSize: 20,
  lineHeight: 20,
})

const center = css({
  textAlign: 'center',
})

const textSize = css({
  fontSize: 25,
  lineHeight: 30,
})

const textSizeLG = css({
  fontSize: 15,
  lineHeight: 15,
})
const textStyle = css({
  fontFamily: 'Matrice-Bold',
  textAlign: 'center',
  position: 'relative',
})

const Features: React.FC<Props> = (props) => {
  const [theme] = useContext(ThemeContext)

  const {
    intl: { formatMessage },
  } = props
  return (
    <FeaturesLayout>
      <SEO
        title={formatMessage({ id: 'featuresReason.metaTitle' })}
        description={formatMessage({ id: 'featuresReason.description' })}
        urlPage='https://pagily.io/fonctionnalites/gestion-impression/'
      />

      <View minShowSize={LG_SIZE}>
        <Header
          title={formatMessage({ id: 'featuresReason.title' })}
          subtitle={formatMessage({ id: 'featuresReason.subtitle' })}
        />
        <Spacing spacings={['mt-5', 'mb-3']}>
          <Center>
            <>
              <H2 style={[title, theme.styles.mt5]}>
                {formatMessage({ id: 'featuresReason.reason.title' })}
              </H2>
              <View>
                <Text style={[textStyle, textSize, theme.styles.p3]}>
                  {formatMessage({ id: 'featuresReason.reason.subtitle' })}
                  <GreenCircle width={180} height={50} top={55} right={15} />
                </Text>
              </View>
            </>
          </Center>
        </Spacing>
      </View>

      <View maxShowSize={LG_SIZE}>
        <Header
          title={formatMessage({ id: 'featuresReason.title' })}
          subtitle={formatMessage({ id: 'featuresReason.subtitleResp' })}
        />
        <Center>
          <>
            <H2 style={[center, titleLG, theme.styles.mt4]}>
              {formatMessage({ id: 'featuresReason.reason.title' })}
            </H2>
            <View>
              <Text style={[textSizeLG, textStyle, theme.styles.mt3]}>
                {formatMessage({ id: 'featuresReason.reason.subtitleResp' })}
                <GreenCircle width={100} height={40} top={8} right={0} />
              </Text>
            </View>
          </>
        </Center>
      </View>

      <FeaturesImpress
        title={formatMessage({ id: 'featuresReason.reason.item1.title' })}
        content={formatMessage({ id: 'featuresReason.reason.item1.content' })}
        img="/img/demo/reason_1.png"
        blue
        isRight
        isMarginLeft
        alt='Gestion des impressions Cloud sans installation et mobile'
      />

      <FeaturesImpress
        title={formatMessage({ id: 'featuresReason.reason.item2.title' })}
        content={formatMessage({ id: 'featuresReason.reason.item2.content' })}
        img="/img/demo/reas_2.png"
        alt="Authentification par profil sur l'interface embarquée de l'imprimante"
      />

      <FeaturesImpress
        title={formatMessage({ id: 'featuresReason.reason.item3.title' })}
        content={formatMessage({ id: 'featuresReason.reason.item3.content' })}
        img="/img/demo/climateAction.svg"
        yellow
        isRight
        width
        alt="Diminution du gaspillage de papier"
      />
      <BetaSubscription />

      <View minShowSize={LG_SIZE} style={{ position: 'relative' }}>
        <Center>
          <View>
            <H2 style={[center, title, theme.styles.mt5]}>
              {formatMessage({ id: 'featuresPage.documentaryExp.title' })}
              <GreenCircle width={330} height={105} top={35} right={-50} />
            </H2>
          </View>
        </Center>

        <Text style={[textStyle, theme.styles.p3]}>
          {formatMessage({ id: 'featuresPage.documentaryExp.subtitle' })}
        </Text>
      </View>

      <View maxShowSize={LG_SIZE}>
        <Center>
          <View>
            <H2 style={[center, titleLG, theme.styles.mt5]}>
              {formatMessage({ id: 'featuresPage.documentaryExp.title' })}
            </H2>
            <GreenCircle width={130} height={35} top={67} right={-20} />
          </View>
        </Center>

        <Text style={[textStyle, textSizeLG, theme.styles.pt2]}>
          {formatMessage({ id: 'featuresPage.documentaryExp.subtitle' })}
        </Text>
      </View>
      <View style={[theme.styles.flexRowCenter]}>
        <DarkCard
          imgBackground="/img/demo/wave_left.svg"
          img="/img/demo/features_screen4.svg"
          title={formatMessage({
            id: 'featuresPage.documentaryExp.item1.title',
          })}
          link={formatMessage({ id: 'common.more-info' })}
          linkTo={`/fonctionnalites/${formatMessage({
            id: 'urls-archive',
          })}`}
          alt='Archivage intelligent : indexation automatique et valeur probatoire préservée'
        ></DarkCard>
        <DarkCard
          imgBackground="/img/demo/wave_right.svg"
          img="/img/demo/Acquerir_dark.svg"
          title={formatMessage({
            id: 'featuresPage.documentaryExp.item2.universal',
          })}
          link={formatMessage({ id: 'common.more-info' })}
          linkTo={`/fonctionnalites/${formatMessage({
            id: 'urls-universal',
          })}`}
          alt='Numérisation universelle : pour scanner depuis un smartphone ou un multifonctions'
        ></DarkCard>
      </View>
    </FeaturesLayout>
  )
}

export default injectIntl(Features)
